package er.prateek.crossly

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.platform.LocalFontFamilyResolver
import androidx.compose.ui.text.font.FontFamily
import crossly.webapp.generated.resources.Res
import crossly.webapp.generated.resources.noto_color_emoji_subset
import crossly.webapp.generated.resources.noto_sans_math_subset
import crossly.webapp.generated.resources.noto_sans_symbols2_subset
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.preloadFont

/** Past this, the app starts without the fallbacks rather than sit on a blank page. */
private const val FONT_WAIT_MS = 3_000L

/**
 * Holds the first frame until the emoji and symbol fallback fonts are registered.
 *
 * Compose for Web draws text with skiko's one built-in font, which has no emoji and few symbols,
 * so without these "Cooked 🍳" or "Alphabets → Maths" render as empty boxes. Android and iOS fall
 * back to the system fonts and don't need this. The fonts are ~46 KB subsets of Noto holding only
 * the characters the app uses -- regenerate them with tools/webfonts/build_webfonts.py.
 */
@OptIn(ExperimentalResourceApi::class)
@Composable
fun WithWebFallbackFonts(content: @Composable () -> Unit) {
    // Emoji first: a character in more than one font (✋, 🎭) then comes out in colour.
    val emoji by preloadFont(Res.font.noto_color_emoji_subset)
    val symbols by preloadFont(Res.font.noto_sans_symbols2_subset)
    val math by preloadFont(Res.font.noto_sans_math_subset)
    val resolver = LocalFontFamilyResolver.current
    var ready by remember { mutableStateOf(false) }

    LaunchedEffect(emoji, symbols, math) {
        val fonts = listOf(emoji, symbols, math)
        if (fonts.any { it == null }) return@LaunchedEffect
        fonts.forEach { resolver.preload(FontFamily(it!!)) }
        ready = true
    }
    // A failed or stalled download must never keep the game from starting.
    LaunchedEffect(Unit) {
        delay(FONT_WAIT_MS)
        ready = true
    }

    if (ready) content()
}
